import { FormEvent, KeyboardEvent, MouseEvent, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Card, Details, Dialog, Paragraph } from '@digdir/designsystemet-react'
import { useCertificateDownload, useVessel } from '@hooks'
import { CertificateDownloadInput, LoadingStatus, PageBreadcrumbs } from '@components/Shared'
import { vesselService } from '@services'
import { getValidFrom, getValidToDisplay } from '@utils'
import { useLanguage } from '@i18n'
import type { Certificate } from '@app-types/index'
import './Vessel.css'

const getCertificateTypeName = (
  certificate: Certificate,
  t: (key: string) => string
) => {
  if (!certificate.type) {
    return certificate.name || ''
  }

  if (certificate.name && certificate.name !== certificate.type) {
    return certificate.name
  }

  const translatedName = t(`certificateTypes.${certificate.type}`)
  return translatedName === `certificateTypes.${certificate.type}` ? '' : translatedName
}

export const Vessel = () => {
  const { vesselID } = useParams<{ vesselID: string }>()
  const navigate = useNavigate()
  const { t } = useLanguage()
  const { vessel, availableOffline, setAvailableOffline, loading, error } = useVessel(vesselID)
  const [downloadCertificate, setDownloadCertificate] = useState<Certificate | null>(null)
  const [utnNumber, setUtnNumber] = useState('')
  const {
    clearDownloadError,
    downloadCertificate: downloadSelectedCertificate,
    downloadError,
    isDownloading,
  } = useCertificateDownload()
  const certificateLabel = useMemo(
    () => downloadCertificate?.type || downloadCertificate?.name || t('certificate.titleFallback').toLowerCase(),
    [downloadCertificate, t]
  )
  const offlineActionLabel = useMemo(() => t(availableOffline ? 'vessel.remove' : 'vessel.save'), [availableOffline, t])
  const offlineButtonLabel = useMemo(
    () => t(availableOffline ? 'vessel.savedOffline' : 'vessel.saveOffline'),
    [availableOffline, t]
  )

  const handleOfflineToggle = async () => {
    if (!vessel) {
      return
    }

    if (availableOffline) {
      await vesselService.remove(vessel.vesselID)
      setAvailableOffline(false)
      return
    }

    await vesselService.save(vessel)
    setAvailableOffline(true)
  }

  const certificates = vessel?.certificates || []

  useEffect(() => {
    setUtnNumber(downloadCertificate?.seqNo || '')
    clearDownloadError()
  }, [clearDownloadError, downloadCertificate])

  const openCertificate = (certificate: Certificate) => {
    if (!vessel) {
      return
    }

    navigate(`/vessels/vessel/${vessel.vesselID}/${certificate.uuid}`)
  }

  const openDownloadDialog = (event: MouseEvent<HTMLButtonElement>, certificate: Certificate) => {
    event.stopPropagation()
    setDownloadCertificate(certificate)
  }

  const closeDownloadDialog = () => {
    if (isDownloading) {
      return
    }

    setDownloadCertificate(null)
    clearDownloadError()
  }

  const handleDownloadSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (!downloadCertificate || !vessel) {
      return
    }

    void downloadSelectedCertificate(downloadCertificate, utnNumber, {
      onComplete: () => setDownloadCertificate(null),
      requireUtn: true,
      vesselName: vessel.vesselName,
    })
  }

  const handleRowKeyDown = (event: KeyboardEvent<HTMLTableRowElement>, certificate: Certificate) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return
    }

    event.preventDefault()
    openCertificate(certificate)
  }

  if (loading) {
    return <LoadingStatus className="vessel loading" label={t('vessel.loading')} />
  }

  if (error) {
    return <div className="vessel error">{t('vessel.errorLoading', { message: error.message })}</div>
  }

  if (!vessel) {
    return <div className="vessel not-found">{t('vessel.notFound')}</div>
  }

  return (
    <section className="vessel">
      <PageBreadcrumbs
        backLabel={t('breadcrumbs.backToVessels')}
        backTo="/vessels"
        items={[
          { label: t('breadcrumbs.vessels'), to: '/vessels' },
          { label: vessel.vesselName, to: `/vessels/vessel/${vessel.vesselID}` },
        ]}
      />

      <div className="vessel__hero">
        <div className="vessel__summary">
          <div className="vessel__title-row">
            <h1>{vessel.vesselName}</h1>
            <button
              className="vessel__offline-action"
              type="button"
              aria-pressed={availableOffline}
              aria-label={t('vessel.availableOfflineLabel', {
                action: offlineActionLabel,
                vesselName: vessel.vesselName,
              })}
              onClick={() => {
                void handleOfflineToggle()
              }}
            >
              <span
                className={
                  availableOffline
                    ? 'vessel__icon vessel__icon--check'
                    : 'vessel__icon vessel__icon--save-offline'
                }
                aria-hidden="true"
              />
              {offlineButtonLabel}
            </button>
          </div>
          <div className="vessel__summary-meta" aria-label={t('vessel.vesselDetails')}>
            <span>{t('vessel.certificatesFound', { count: certificates.length })}</span>
            {vessel.imoNumber && <span>IMO {vessel.imoNumber}</span>}
          </div>
        </div>
      </div>

      <div className="vessel__table-card">
        <table className="vessel__table">
          <caption className="visually-hidden">
            {t('vessel.tableCaption', { vesselName: vessel.vesselName })}
          </caption>
          <thead>
            <tr>
              <th scope="col">{t('common.type')}</th>
              <th scope="col">{t('common.validity')}</th>
              <th scope="col">{t('common.status')}</th>
              <th scope="col">
                <span className="vessel__download-heading-label">{t('common.download')}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {certificates.length > 0 ? (
              certificates.map((certificate) => {
                const certificateType = certificate.type || certificate.name || '-'
                const certificateTypeName = getCertificateTypeName(certificate, t)

                return (
                  <tr
                    key={certificate.uuid}
                    className="vessel__certificate-row"
                    tabIndex={0}
                    onClick={() => openCertificate(certificate)}
                    onKeyDown={(event) => handleRowKeyDown(event, certificate)}
                  >
                    <td data-label={t('common.type')}>
                      <span className="vessel__certificate-type">
                        <span
                          className="vessel__cell-link"
                          title={certificateTypeName || undefined}
                          aria-label={certificateTypeName ? `${certificateType}: ${certificateTypeName}` : undefined}
                        >
                          {certificateType}
                        </span>
                        {certificateTypeName && (
                          <span className="vessel__certificate-type-name">{certificateTypeName}</span>
                        )}
                      </span>
                    </td>
                    <td data-label={t('common.validity')}>
                      <span className="vessel__validity">
                        <span>{getValidFrom(certificate)}</span>
                        <span aria-hidden="true">→</span>
                        <span>{getValidToDisplay(certificate, t('vessel.noExpiry'))}</span>
                      </span>
                    </td>
                    <td data-label={t('common.status')}>
                      <span className="vessel__status">
                        {certificate.status || '-'}
                      </span>
                    </td>
                    <td data-label={t('common.download')}>
                      <button
                        className="vessel__download"
                        type="button"
                        aria-label={t('vessel.downloadAriaLabel', {
                          certificateName: certificate.type || certificate.name || t('certificate.titleFallback').toLowerCase(),
                        })}
                        onClick={(event) => openDownloadDialog(event, certificate)}
                      >
                        <span className="vessel__icon vessel__icon--download" aria-hidden="true" />
                      </button>
                    </td>
                  </tr>
                )
              })
            ) : (
              <tr>
                <td colSpan={4} className="vessel__empty">
                  {t('vessel.noCertificatesFound')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Card className="vessel__faq" aria-label={t('vessel.faqAriaLabel')}>
        <Details className="vessel__faq-item">
          <Details.Summary>{t('vessel.offlineQuestion')}</Details.Summary>
          <Details.Content className="vessel__faq-content">
            {t('vessel.offlineText')}
          </Details.Content>
        </Details>
      </Card>

      <Dialog
        className="vessel__download-dialog"
        open={downloadCertificate !== null}
        onClose={closeDownloadDialog}
        closedby="any"
        closeButton={t('common.close')}
      >
        <form onSubmit={handleDownloadSubmit}>
          <Dialog.Block>
            <Paragraph data-size="sm">
              {t('vessel.downloadDialog', { certificateLabel })}
            </Paragraph>
          </Dialog.Block>

          <Dialog.Block className="vessel__download-dialog-control">
            <CertificateDownloadInput
              id="vessel-download-utn"
              label={t('certificateDownload.inputLabel')}
              value={utnNumber}
              error={downloadError}
              loading={isDownloading}
              disabled={!utnNumber.trim()}
              onChange={(value) => {
                setUtnNumber(value)
                clearDownloadError()
              }}
            />
          </Dialog.Block>
        </form>
      </Dialog>
    </section>
  )
}

export default Vessel
